/*
 * Copyright (C) 2018 Frontier Developments PLC.
 */

import {Component, Vue} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';
import {
    UgcBuild,
    UgcBuildInterface,
    UgcBuildPresignedResponseInterface,
    UgcRevisionInterface,
} from '@/store/modules/ugc-build/types';
import RevisionDetails from '@/components/RevisionDetails/index.vue';
import RevisionError from '@/components/RevisionError/index.vue';
import BuildThumbnail from '@/components/BuildThumbnail/index.vue';
import ApiException from '@/exceptions/ApiException';

const namespaceUgcBuild: string = 'ugcBuildModule';
const namespaceSettings: string = 'settingsModule';

/**
 * Revision list component.
 *
 * This represents a list of user item revisions that have been added to the system.
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
@Component({
    name: 'c-revision-list',
    components: {
        'c-revision-details': RevisionDetails,
        'c-revision-error': RevisionError,
        'c-build-thumbnail': BuildThumbnail,
    },
})
export default class RevisionList extends Vue {
    @Getter('revisions', {namespace: namespaceUgcBuild}) public revisions!: UgcRevisionInterface[];
    @Getter('hasRevisions', {namespace: namespaceUgcBuild}) public hasRevisions!: boolean;
    @Action('presigned', {namespace: namespaceUgcBuild}) public presigned: any;
    @Action('deleteRevision', {namespace: namespaceUgcBuild}) public deleteRevision: any;
    @Getter('hasMedia', {namespace: namespaceSettings}) public hasMedia!: (name: string) => boolean;

    public selectedRevision: UgcBuildInterface = new UgcBuild();

    /**
     * Artifact created checker.
     *
     * @param {UgcRevisionInterface} revision
     *
     * @returns {boolean}
     */
    public isComplete(revision: UgcRevisionInterface): boolean {
        return 'complete' === revision.status.toLowerCase();
    }

    /**
     * Artifact processing checker.
     *
     * @param {UgcRevisionInterface} revision
     * @returns {boolean}
     */
    public isProcessing(revision: UgcRevisionInterface): boolean {
        return 'processing' === revision.status.toLowerCase();
    }

    /**
     * Artifact uploading checker.
     *
     * @param {UgcRevisionInterface} revision
     *
     * @returns {boolean}
     */
    public isUploading(revision: UgcRevisionInterface): boolean {
        return 'uploading' === revision.status.toLowerCase();
    }

    /**
     * Artifact in-queue checker.
     *
     * @param {UgcRevisionInterface} revision
     *
     * @returns {boolean}
     */
    public isInQueue(revision: UgcRevisionInterface): boolean {
        return 'in-queue' === revision.status.toLowerCase();
    }

    /**
     * Artifact building checker.
     *
     * @param {UgcRevisionInterface} revision
     *
     * @returns {boolean}
     */
    public isBuilding(revision: UgcRevisionInterface): boolean {
        return 'building' === revision.status.toLowerCase();
    }

    /**
     * Artifact rejected checker.
     *
     * @param {UgcRevisionInterface} revision
     *
     * @returns {boolean}
     */
    public isRejected(revision: UgcRevisionInterface): boolean {
        return 'rejected' === revision.status.toLowerCase();
    }

    /**
     * Artifact failed checker.
     *
     * @param {UgcRevisionInterface} revision
     *
     * @returns {boolean}
     */
    public isFailed(revision: UgcRevisionInterface): boolean {
        return 'failed' === revision.status.toLowerCase();
    }

    /**
     * Download the artifact.
     *
     * @param {UgcRevisionInterface} build
     * @returns {Promise<void>}
     */
    public async downloadArtifact(build: UgcRevisionInterface): Promise<void> {
        try {
            const method: string = 'GET';
            const presigned: UgcBuildPresignedResponseInterface = await this.presigned({build, method});

            if (undefined === presigned) {
                throw new Error('File could not be found');
            }

            window.location.href = presigned.presigned_url;
        } catch (exception) {
            if (exception instanceof ApiException && 404 === exception.status) {
                this.$emit('list-update');
            }

            this.$toastr.error(this.$i18n.translate('notification.revision.download.failed', {}) || '');
        }
    }

    /**
     * Show the details modal.
     *
     * @param {string} revisionId
     */
    public showDetailsModal(revision: UgcBuildInterface): void {
        Vue.set(this, 'selectedRevision', revision);
        this.$modal.show('revision-details');
    }

    /**
     * Show the error modal.
     *
     * @param {string} revisionId
     */
    public showErrorModal(revision: UgcBuildInterface): void {
        Vue.set(this, 'selectedRevision', revision);
        this.$modal.show('revision-error');
    }

    /**
     * Remove revision action event.
     *
     * @param {UgcRevisionInterface} revision
     *
     * @returns {Promise<void>}
     */
    public async removeRevision(revision: UgcRevisionInterface): Promise<void> {
        const name: string = revision.originalItem ? revision.originalItem.name : '';
        this.$modal.show('dialog', {
            title: `<h2>${this.$i18n.translate('modal.delete.revision.title', {})}</h2>`,
            text: this.$i18n.translate('modal.delete.revision.text', {name}),
            buttons: [
                {
                    title: `<span role="confirm">${this.$i18n.translate('modal.delete.revision.confirm', {})}</span>`,
                    handler: (): void => {
                        this.$modal.hide('dialog');
                        this.actionRemoveRevision(revision);
                    },
                },
                {
                    title: `<span role="cancel">${this.$i18n.translate('modal.delete.revision.cancel', {})}</span>`,
                    default: true,
                },
            ],
        });
    }

    /**
     * Action the revision removal.
     *
     * @param {UgcRevisionInterface} revision
     *
     * @returns {Promise<void>}
     */
    private async actionRemoveRevision(revision: UgcRevisionInterface): Promise<void> {
        try {
            await this.deleteRevision(revision);
            this.$toastr.success(
                this.$i18n.translate('notification.revision.deletion.success', {}) || '');
        } catch (exception) {
            if (exception instanceof ApiException && 404 === exception.status) {
                this.$emit('list-update');
            }

            this.$toastr.error(
                this.$i18n.translate('notification.revision.deletion.failed', {}) || '');
        }
    }}

